'use client';

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Globe, 
  Crown, 
  FileText,
  Shield,
  CaretRight,
  Check,
  Upload,
  Spinner,
  X,
  Key,
} from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { QRCodeSVG } from 'qrcode.react';
import { useAuthStore } from '../stores/authStore';
import { useLanguageStore, SUPPORTED_LANGUAGES, type LanguageCode } from '../stores/languageStore';
import { isLocalDeployment } from '../config';
import { subscriptionApi, licenseApi } from '../services/api';
import { LicensePage } from './LicensePage';
import { useToastActions } from '../components/ui/Toast';
import { getAvatarUrl } from '../utils/avatar';

// 格式化字节
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// 计划名称映射
const planNames: Record<string, string> = {
  free: '免费版',
  pro: '专业版',
  enterprise: '企业版',
};

// 计划月费
const planPrices: Record<string, number> = {
  free: 0,
  pro: 99,
  enterprise: 999,
};

// ==================== 主设置页面 ====================

interface SettingsItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>;
  iconColor: string;
  iconBgColor: string;
  onClick: () => void;
  rightContent?: React.ReactNode;
}

export function SettingsPage() {
  const { user } = useAuthStore();
  const [currentView, setCurrentView] = useState<'main' | 'profile' | 'subscription' | 'terms' | 'privacy' | 'license'>('main');
  const [subscription, setSubscription] = useState<{
    plan: 'free' | 'pro' | 'enterprise';
    currentMonthExecutions: number;
    maxWorkflowExecutions: number;
    currentStorageBytes: number;
    maxStorageBytes: number;
  } | null>(null);
  // 语言状态 - 使用全局状态
  const { language, setLanguage } = useLanguageStore();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [languageDropdownPosition, setLanguageDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const languageTriggerRef = useRef<HTMLDivElement>(null);

  const currentLanguage = SUPPORTED_LANGUAGES.find(l => l.code === language);

  const openLanguageDropdown = () => {
    if (languageTriggerRef.current) {
      const rect = languageTriggerRef.current.getBoundingClientRect();
      const menuHeight = 180;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;
      setLanguageDropdownPosition({
        top: openAbove ? rect.top - menuHeight - 8 : rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
      setShowLanguageDropdown(true);
    }
  };

  const closeLanguageDropdown = () => {
    setShowLanguageDropdown(false);
    setLanguageDropdownPosition(null);
  };

  // 点击外部关闭语言下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showLanguageDropdown && !languageTriggerRef.current?.contains(target) && !target.closest('[data-language-dropdown]')) {
        closeLanguageDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown]);

  // 加载订阅数据
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const res = await subscriptionApi.getCurrentSubscription();
        if (res.code === 0) {
          setSubscription(res.data);
        }
      } catch (error) {
        console.error('Failed to load subscription:', error);
      }
    };
    loadSubscription();
  }, []);

  // License 状态（本地部署时显示）
  const [licenseStatus, setLicenseStatus] = useState<string>('');

  // 加载 License 状态（本地部署时）
  useEffect(() => {
    if (isLocalDeployment) {
      licenseApi.getLicenseStatus().then((res) => {
        if (res.code === 0 && res.data) {
          const statusMap: Record<string, string> = {
            active: '已激活',
            grace_period: '宽限期',
            expired: '已过期',
            revoked: '已吊销',
            invalid: '无效',
          };
          setLicenseStatus(statusMap[res.data.license.status] || '未激活');
        }
      }).catch(() => {
        setLicenseStatus('未激活');
      });
    }
  }, []);

  // 设置项配置（本地化部署时隐藏订阅管理、服务条款、隐私政策）
  const licenseItem: SettingsItem = {
    id: 'license',
    title: 'License 授权',
    description: licenseStatus || '查看授权状态',
    icon: Key,
    iconColor: 'text-amber-600',
    iconBgColor: 'bg-amber-100',
    onClick: () => setCurrentView('license'),
  };

  const allSettingsItems: SettingsItem[] = [
    ...(isLocalDeployment ? [licenseItem] : []),
    {
      id: 'profile',
      title: '用户信息',
      description: user?.username || '点击编辑个人资料',
      icon: User,
      iconColor: 'text-emerald-600',
      iconBgColor: 'bg-emerald-100',
      onClick: () => setCurrentView('profile'),
    },
    {
      id: 'language',
      title: '语言',
      description: currentLanguage?.name || '简体中文',
      icon: Globe,
      iconColor: 'text-blue-600',
      iconBgColor: 'bg-blue-100',
      onClick: () => openLanguageDropdown(),
      rightContent: (
        <div ref={languageTriggerRef} onClick={(e) => { e.stopPropagation(); openLanguageDropdown(); }} className="flex items-center">
          <span className="text-lg mr-2">{currentLanguage?.flag}</span>
        </div>
      ),
    },
    {
      id: 'subscription',
      title: '订阅管理',
      description: `${planNames[subscription?.plan || 'free']} · 本月已用 ${subscription?.currentMonthExecutions || 0} 次`,
      icon: Crown,
      iconColor: 'text-amber-600',
      iconBgColor: 'bg-amber-100',
      onClick: () => setCurrentView('subscription'),
      rightContent: subscription && (
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>存储 {formatBytes(subscription.currentStorageBytes)}</span>
        </div>
      ),
    },
    {
      id: 'terms',
      title: '服务条款',
      description: 'Terms of Service',
      icon: FileText,
      iconColor: 'text-blue-600',
      iconBgColor: 'bg-blue-100',
      onClick: () => setCurrentView('terms'),
    },
    {
      id: 'privacy',
      title: '隐私政策',
      description: 'Privacy Policy',
      icon: Shield,
      iconColor: 'text-purple-600',
      iconBgColor: 'bg-purple-100',
      onClick: () => setCurrentView('privacy'),
    },
  ];

  const settingsItems = isLocalDeployment
    ? allSettingsItems.filter((item) => !['subscription', 'terms', 'privacy'].includes(item.id))
    : allSettingsItems;

  // 渲染子页面
  if (currentView === 'profile') {
    return <ProfilePage onBack={() => setCurrentView('main')} />;
  }
  if (currentView === 'subscription') {
    return <SubscriptionPage onBack={() => setCurrentView('main')} />;
  }
  if (currentView === 'terms') {
    return <TermsPage onBack={() => setCurrentView('main')} />;
  }
  if (currentView === 'privacy') {
    return <PrivacyPage onBack={() => setCurrentView('main')} />;
  }
  if (currentView === 'license') {
    return <LicensePage onBack={() => setCurrentView('main')} />;
  }

  // 主设置页面
  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-zinc-900">设置</h1>
          <p className="text-sm text-zinc-500 mt-1">管理您的账户和偏好设置</p>
        </div>

        {/* 设置列表 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 overflow-hidden">
          {settingsItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={item.onClick}
                className={`w-full flex items-center gap-4 p-4 text-left transition-colors hover:bg-zinc-50 ${
                  index !== settingsItems.length - 1 ? 'border-b border-zinc-100' : ''
                }`}
              >
                <div className={`w-10 h-10 rounded-xl ${item.iconBgColor} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 ${item.iconColor}`} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900">{item.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{item.description}</p>
                </div>
                {item.rightContent}
                <CaretRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
              </button>
            );
          })}
        </div>

        {/* 语言下拉 - Portal 渲染，避免 overflow 裁剪 */}
        {showLanguageDropdown && languageDropdownPosition && createPortal(
          <AnimatePresence>
            <div
              className="fixed inset-0 z-[100]"
              onClick={closeLanguageDropdown}
            />
            <motion.div
              data-language-dropdown
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="fixed w-48 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-[110]"
              style={{
                top: languageDropdownPosition.top,
                right: languageDropdownPosition.right,
              }}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLanguage(lang.code as LanguageCode);
                    closeLanguageDropdown();
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                  {language === lang.code && (
                    <Check className="w-4 h-4 ml-auto" weight="bold" />
                  )}
                </button>
              ))}
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

        {/* 版本信息 */}
        <div className="mt-8 text-center">
          <p className="text-xs text-zinc-400">DataFlowEngine v1.0.0</p>
          <p className="text-xs text-zinc-400 mt-1">© 2026 All rights reserved.</p>
        </div>
      </motion.div>
    </div>
  );
}

// ==================== 用户信息编辑页面 ====================

function ProfilePage({ onBack }: { onBack: () => void }) {
  const { user, setUser } = useAuthStore();
  const { success: toastSuccess, error: toastError } = useToastActions();
  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    try {
      // TODO: 调用 API 保存用户信息
      // await authApi.updateProfile({ username, email });
      toastSuccess('资料已保存');
      setIsEditing(false);
    } catch (error) {
      toastError('保存失败');
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      toastError('请选择图片文件');
      return;
    }

    // 验证文件大小 (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toastError('图片大小不能超过 2MB');
      return;
    }

    setIsUploading(true);
    try {
      // 上传到服务器
      const formData = new FormData();
      formData.append('avatar', file);
      
      const token = localStorage.getItem('token') || '';
      const baseURL = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1';
      
      const response = await fetch(`${baseURL}/auth/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('上传失败');
      }

      const result = await response.json();
      if (result.code === 0) {
        // 将相对路径转换为完整 URL
        const avatarPath = result.data.avatarUrl;
        const newAvatarUrl = avatarPath.startsWith('http') 
          ? avatarPath 
          : `${baseURL.replace('/api/v1', '')}${avatarPath}`;
        setAvatar(newAvatarUrl);
        // 同步更新 authStore 中的用户信息
        if (user) {
          setUser({ ...user, avatar: newAvatarUrl });
        }
        toastSuccess('头像上传成功');
      } else {
        throw new Error(result.message || '上传失败');
      }
    } catch (error) {
      toastError('头像上传失败');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <CaretRight className="w-5 h-5 text-zinc-600 rotate-180" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">用户信息</h1>
            <p className="text-sm text-zinc-500">管理您的个人资料</p>
          </div>
        </div>

        {/* 内容 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 space-y-6">
          {/* 头像上传 */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div 
                onClick={handleAvatarClick}
                className="w-24 h-24 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center border-2 border-emerald-200 cursor-pointer overflow-hidden group"
              >
                {avatar ? (
                  <img 
                    src={getAvatarUrl(avatar)} 
                    alt="Avatar" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-emerald-600" weight="fill" />
                )}
                
                {/* 悬停遮罩 */}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {isUploading ? (
                    <Spinner className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Upload className="w-6 h-6 text-white" />
                  )}
                </div>
              </div>
              
              {/* 编辑按钮 */}
              <button
                onClick={handleAvatarClick}
                disabled={isUploading}
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-zinc-900 text-white rounded-lg flex items-center justify-center shadow-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {isUploading ? (
                  <Spinner className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
              </button>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              onChange={handleFileChange}
              className="hidden"
            />
            
            <p className="text-xs text-zinc-400">点击上传头像，支持 JPG、PNG 格式，最大 2MB</p>
          </div>

          {/* 表单 */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-2 block">用户名</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:bg-zinc-50 disabled:text-zinc-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-2 block">邮箱</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:bg-zinc-50 disabled:text-zinc-500"
              />
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-100">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  保存
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-800 transition-colors"
              >
                编辑资料
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ==================== 订阅管理页面 ====================

function SubscriptionPage({ onBack }: { onBack: () => void }) {
  const [subscription, setSubscription] = useState<{
    plan: 'free' | 'pro' | 'enterprise';
    status: string;
    startedAt?: string;
    expiresAt?: string;
    maxWorkflowExecutions: number;
    maxStorageBytes: number;
    currentMonthExecutions: number;
    currentStorageBytes: number;
  } | null>(null);
  const [usageStats, setUsageStats] = useState<{
    executionUsagePercent: number;
    storageUsagePercent: number;
  } | null>(null);
  const [plans, setPlans] = useState<{
    plan: 'free' | 'pro' | 'enterprise';
    name: string;
    maxWorkflowExecutions: number;
    maxStorageBytes: number;
    features: string[];
  }[]>([]);
  const [wechatpayEnabled, setWechatpayEnabled] = useState(false);
  const [wechatpaySigning, setWechatpaySigning] = useState<{ [k: string]: boolean }>({});
  const [billingRecords, setBillingRecords] = useState<Array<{ id: string; date: string; amount: number; status: string }>>([]);
  const [loading, setLoading] = useState(true);
  // 支付流程弹窗
  const [showTermsConfirm, setShowTermsConfirm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<'pro' | 'enterprise' | null>(null);
  const [paymentRedirectUrl, setPaymentRedirectUrl] = useState<string | null>(null);
  const [paymentExpireSeconds, setPaymentExpireSeconds] = useState(60);
  const { success: toastSuccess, error: toastError } = useToastActions();

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const [subRes, usageRes, plansRes, wpRes, billingRes] = await Promise.all([
        subscriptionApi.getCurrentSubscription(),
        subscriptionApi.getUsageStats(),
        subscriptionApi.getAllPlans(),
        subscriptionApi.isWechatpayEnabled().catch(() => ({ code: 0, data: { enabled: false } })),
        subscriptionApi.getBillingHistory().catch(() => ({ code: 0, data: [] })),
      ]);

      if (subRes.code === 0) setSubscription(subRes.data);
      if (usageRes.code === 0) setUsageStats(usageRes.data);
      if (plansRes.code === 0) setPlans(plansRes.data);
      if (wpRes?.code === 0 && wpRes?.data) setWechatpayEnabled(wpRes.data.enabled);
      if (billingRes?.code === 0 && billingRes?.data) setBillingRecords(billingRes.data);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 发起支付流程：先显示条款确认，再显示支付弹窗
  const initiatePaymentFlow = (plan: 'pro' | 'enterprise') => {
    setPendingPlan(plan);
    setShowTermsConfirm(true);
  };

  // 用户同意条款后，调用 API 并显示支付弹窗（仅微信支付时使用）
  const onTermsConfirm = async () => {
    if (!pendingPlan) return;
    setShowTermsConfirm(false);
    const plan = pendingPlan;
    try {
      setWechatpaySigning((s) => ({ ...s, [plan]: true }));
      const res = await subscriptionApi.createWechatpaySign(plan);
      if (res.code === 0 && res.data?.redirectUrl) {
        setPaymentRedirectUrl(res.data.redirectUrl);
        setPaymentExpireSeconds(60);
        setShowPaymentModal(true);
        toastSuccess('请扫码完成微信支付授权');
      } else {
        toastError((res as { message?: string })?.message || '创建签约失败');
        setPendingPlan(null);
      }
    } catch (err: unknown) {
      console.error('WeChat sign failed:', err);
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      toastError(msg || '创建签约失败');
      setPendingPlan(null);
    } finally {
      setWechatpaySigning((s) => ({ ...s, [plan]: false }));
    }
  };

  // 升级流程（含条款确认，用于 直接升级 或 微信支付订阅）
  const handleUpgradeWithConfirm = (plan: 'pro' | 'enterprise') => {
    if (wechatpayEnabled) {
      initiatePaymentFlow(plan);
    } else {
      setPendingPlan(plan);
      setShowTermsConfirm(true);
    }
  };

  // 条款确认后的升级（非微信支付时直接调用升级接口）
  const onTermsConfirmForDirectUpgrade = async () => {
    if (!pendingPlan) return;
    setShowTermsConfirm(false);
    if (!wechatpayEnabled) {
      try {
        const res = await subscriptionApi.upgradeSubscription(pendingPlan, 1);
        if (res.code === 0) {
          toastSuccess('订阅升级成功');
          loadSubscriptionData();
        }
      } catch (error) {
        toastError('升级失败');
      }
      setPendingPlan(null);
      return;
    }
    onTermsConfirm();
  };

  // 支付弹窗倒计时
  useEffect(() => {
    if (!showPaymentModal || paymentExpireSeconds <= 0) return;
    const timer = setInterval(() => {
      setPaymentExpireSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [showPaymentModal, paymentExpireSeconds]);

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentRedirectUrl(null);
    setPaymentExpireSeconds(60);
    setPendingPlan(null);
  };

  // 取消微信自动续费
  const handleTerminateWechatpay = async (plan: 'pro' | 'enterprise') => {
    try {
      const res = await subscriptionApi.terminateWechatpayContract(plan);
      if (res.code === 0) {
        toastSuccess('已取消自动续费');
        loadSubscriptionData();
      }
    } catch (error) {
      console.error('Terminate failed:', error);
      toastError('取消失败');
    }
  };

  // 取消计划（综合处理微信支付解约与普通取消）
  const handleCancelPlan = async () => {
    const plan = subscription?.plan || 'free';
    if (plan === 'free') return;
    try {
      if (wechatpayEnabled) {
        await handleTerminateWechatpay(plan as 'pro' | 'enterprise');
      } else {
        const res = await subscriptionApi.cancelSubscription();
        if (res.code === 0) {
          toastSuccess('已取消订阅');
          loadSubscriptionData();
        }
      }
    } catch (error) {
      toastError('取消失败');
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-zinc-50 flex items-center justify-center">
        <Spinner className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  const currentPlan = subscription?.plan || 'free';

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <CaretRight className="w-5 h-5 text-zinc-600 rotate-180" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">订阅管理</h1>
            <p className="text-sm text-zinc-500">管理您的订阅计划</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* 当前计划信息 */}
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl border border-emerald-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-600">当前计划</p>
                <p className="text-2xl font-bold text-zinc-900">{planNames[currentPlan]}</p>
                {subscription?.expiresAt && (
                  <p className="text-xs text-zinc-500 mt-1">
                    到期时间：{new Date(subscription.expiresAt).toLocaleDateString('zh-CN')}
                  </p>
                )}
                <p className="text-xs text-zinc-500 mt-1">
                  下次扣费日期：{subscription?.status === 'active' && subscription?.expiresAt && (currentPlan === 'pro' || currentPlan === 'enterprise')
                    ? new Date(subscription.expiresAt).toLocaleDateString('zh-CN')
                    : '-'}
                </p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <Crown className="w-7 h-7 text-emerald-600" weight="fill" />
              </div>
            </div>
          </div>

          {/* 用量统计 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <h3 className="text-sm font-medium text-zinc-900 mb-4">本月用量</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-zinc-600">工作流执行</span>
                  <span className="text-zinc-900 font-medium">
                    {subscription?.currentMonthExecutions || 0} / {subscription?.maxWorkflowExecutions === -1 ? '无限' : subscription?.maxWorkflowExecutions || 100}
                  </span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all" 
                    style={{ width: `${Math.min(usageStats?.executionUsagePercent || 0, 100)}%` }} 
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-zinc-600">数据存储</span>
                  <span className="text-zinc-900 font-medium">
                    {formatBytes(subscription?.currentStorageBytes || 0)} / {formatBytes(subscription?.maxStorageBytes || 0)}
                  </span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all" 
                    style={{ width: `${Math.min(usageStats?.storageUsagePercent || 0, 100)}%` }} 
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 计划选择 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <h3 className="text-sm font-medium text-zinc-900 mb-4">升级计划</h3>
            <div className="space-y-3">
              {plans.map((plan) => (
                <div
                  key={plan.plan}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    currentPlan === plan.plan
                      ? 'border-emerald-500 bg-emerald-50/30'
                      : 'border-zinc-100 hover:border-zinc-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-zinc-900">{plan.name}</h4>
                      <p className="text-xs text-zinc-500">
                        {plan.maxWorkflowExecutions === -1 ? '无限执行' : `每月 ${plan.maxWorkflowExecutions} 次执行`} · {formatBytes(plan.maxStorageBytes)} 存储
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold text-zinc-900">
                        {plan.plan === 'free' ? '¥0' : plan.plan === 'pro' ? '¥99' : '¥999'}
                      </span>
                      <span className="text-xs text-zinc-500">/月</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-1 mb-4">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-zinc-600">
                        <Check className="w-3 h-3 text-emerald-500" weight="bold" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {plan.plan === 'free' ? (
                    <button
                      disabled
                      className="w-full py-2 text-xs font-medium rounded-lg bg-emerald-100 text-emerald-700 cursor-default"
                    >
                      当前计划
                    </button>
                  ) : wechatpayEnabled ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpgradeWithConfirm(plan.plan as 'pro' | 'enterprise')}
                        disabled={currentPlan === plan.plan || wechatpaySigning[plan.plan]}
                        className="flex-1 py-2 text-xs font-medium rounded-lg bg-[#07c160] text-white hover:bg-[#06ad56] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                      >
                        {wechatpaySigning[plan.plan] ? (
                          <>
                            <Spinner className="w-3 h-3 animate-spin" />
                            生成中...
                          </>
                        ) : (
                          '微信支付订阅'
                        )}
                      </button>
                      <button
                        onClick={() => handleUpgradeWithConfirm(plan.plan as 'pro' | 'enterprise')}
                        disabled={currentPlan === plan.plan}
                        className="flex-1 py-2 text-xs font-medium rounded-lg border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                      >
                        直接升级
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleUpgradeWithConfirm(plan.plan as 'pro' | 'enterprise')}
                      disabled={currentPlan === plan.plan}
                      className={`w-full py-2 text-xs font-medium rounded-lg transition-colors ${
                        currentPlan === plan.plan
                          ? 'bg-emerald-100 text-emerald-700 cursor-default'
                          : 'bg-zinc-900 text-white hover:bg-zinc-800'
                      }`}
                    >
                      {currentPlan === plan.plan ? '当前计划' : '升级'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 当前订阅信息 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <h3 className="text-sm font-medium text-zinc-900 mb-4">当前订阅信息</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">订阅计划</span>
                <span className="text-sm font-medium text-zinc-900">{planNames[currentPlan]}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">状态</span>
                <span className={`text-sm font-medium ${
                  subscription?.status === 'active' ? 'text-emerald-600' : 'text-amber-600'
                }`}>
                  {subscription?.status === 'active' ? '活跃' : subscription?.status === 'expired' ? '已过期' : '已取消'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">开始日期</span>
                <span className="text-sm font-medium text-zinc-900">
                  {subscription?.startedAt 
                    ? new Date(subscription.startedAt).toLocaleDateString('zh-CN')
                    : '-'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">到期日期</span>
                <span className="text-sm font-medium text-zinc-900">
                  {subscription?.expiresAt 
                    ? new Date(subscription.expiresAt).toLocaleDateString('zh-CN')
                    : '永久有效'}
                </span>
              </div>
              {wechatpayEnabled && (currentPlan === 'pro' || currentPlan === 'enterprise') && (
                <div className="pt-3">
                  <button
                    onClick={() => handleTerminateWechatpay(currentPlan as 'pro' | 'enterprise')}
                    className="text-xs text-amber-600 hover:text-amber-700"
                  >
                    取消微信自动续费
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 账单 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <h3 className="text-sm font-medium text-zinc-900 mb-4">账单</h3>
            <div className="max-h-64 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-zinc-500 border-b border-zinc-200">
                    <th className="pb-3 font-medium">日期</th>
                    <th className="pb-3 font-medium text-right">金额</th>
                    <th className="pb-3 font-medium text-right">状态</th>
                  </tr>
                </thead>
                <tbody>
                  {billingRecords.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-zinc-400 text-sm">
                        暂无账单记录
                      </td>
                    </tr>
                  ) : (
                    billingRecords.map((row) => {
                      const statusText = row.status === 'refunded' ? '已退款' : row.status === 'paid' ? '已支付' : row.status === 'failed' ? '支付失败' : row.status === 'pending' ? '待支付' : row.status;
                      return (
                        <tr key={row.id} className="border-b border-zinc-100 last:border-0">
                          <td className="py-3 text-zinc-900">
                            {new Date(row.date).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                          </td>
                          <td className="py-3 text-zinc-900 text-right">¥{Number(row.amount).toFixed(2)}</td>
                          <td className="py-3 text-right">
                            <span className={row.status === 'refunded' ? 'text-amber-600' : 'text-zinc-900'}>
                              {statusText}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {(currentPlan === 'pro' || currentPlan === 'enterprise') && (
              <div className="mt-4">
                <button
                  onClick={handleCancelPlan}
                  className="w-full py-2.5 text-sm font-medium rounded-lg bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors"
                >
                  取消计划
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* 支付条款确认弹窗 */}
      <Dialog.Root open={showTermsConfirm} onOpenChange={(open) => !open && setShowTermsConfirm(false)}>
        <Dialog.Portal>
          <AnimatePresence>
            {showTermsConfirm && (
              <>
                <Dialog.Overlay asChild>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
                  >
                    <Dialog.Title className="text-lg font-semibold text-zinc-900 mb-4">
                      订阅确认
                    </Dialog.Title>
                    <Dialog.Description asChild>
                      <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
                        支付即表示您同意
                        <a href="#" className="text-emerald-600 hover:underline mx-1">《会员权益与 AI Excel 付费服务协议》</a>
                      </p>
                    </Dialog.Description>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => setShowTermsConfirm(false)}
                        className="px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg"
                      >
                        取消
                      </button>
                      <button
                        onClick={() => onTermsConfirmForDirectUpgrade()}
                        className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      >
                        同意并继续
                      </button>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 微信支付弹窗 */}
      <Dialog.Root open={showPaymentModal} onOpenChange={(open) => !open && closePaymentModal()}>
        <Dialog.Portal>
          <AnimatePresence>
            {showPaymentModal && paymentRedirectUrl && pendingPlan && (
              <>
                <Dialog.Overlay asChild>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <h2 className="text-lg font-semibold text-zinc-900">
                        开通{planNames[pendingPlan]}
                      </h2>
                      <Dialog.Close asChild>
                        <button className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500">
                          <X className="w-5 h-5" />
                        </button>
                      </Dialog.Close>
                    </div>
                    <div className="flex gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-48 h-48 bg-white border border-zinc-200 rounded-xl p-3 flex items-center justify-center">
                          <QRCodeSVG value={paymentRedirectUrl} size={180} level="M" />
                        </div>
                        <p className="text-xs text-zinc-500 mt-3">
                          {paymentExpireSeconds > 0 ? `${paymentExpireSeconds} 秒内有效` : '已过期'}
                        </p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded bg-[#07c160] flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" weight="bold" />
                          </div>
                          <span className="text-sm text-zinc-700">微信支付</span>
                          <span className="text-base font-semibold text-zinc-900">
                            ¥{planPrices[pendingPlan] || 0}
                          </span>
                        </div>
                        <ul className="space-y-2 text-xs text-zinc-500">
                          <li>• 自动续费：¥{planPrices[pendingPlan] || 0} / 月</li>
                          <li>• 到期前一日通过微信自动扣款，可随时在微信中取消</li>
                          <li>• 虚拟商品一经购买不支持退款</li>
                          <li>• 未成年人请在监护人指导下充值</li>
                          <li>
                            • 支付即表示同意
                            <a href="#" className="text-blue-600 hover:underline">《AI Excel 服务协议》</a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

// ==================== 服务条款页面 ====================

function TermsPage({ onBack }: { onBack: () => void }) {
  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <CaretRight className="w-5 h-5 text-zinc-600 rotate-180" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">服务条款</h1>
            <p className="text-sm text-zinc-500">Terms of Service</p>
          </div>
        </div>

        {/* 内容 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 space-y-6 text-sm text-zinc-700">
          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">1. 服务概述</h2>
            <p className="leading-relaxed">
              DataFlow Engine（以下简称"我们"或"本服务"）是一款基于人工智能的 Excel 数据处理引擎，
              提供自然语言意图解析、自动化工作流编排和数据处理服务。通过使用本服务，
              您同意遵守以下服务条款。
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">2. 账户注册与安全</h2>
            <p className="leading-relaxed">
              您需要提供准确、完整的个人信息进行注册。您有责任保护您的账户密码安全，
              并对您账户下的所有活动负责。如发现未经授权的使用，请立即通知我们。
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">3. 使用限制</h2>
            <ul className="list-disc list-inside space-y-2 leading-relaxed">
              <li>不得将本服务用于任何非法目的</li>
              <li>不得上传包含恶意代码的文件</li>
              <li>不得尝试未经授权访问系统</li>
              <li>不得干扰或破坏服务的正常运行</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">4. 订阅与付费</h2>
            <p className="leading-relaxed">
              我们提供免费版和专业版等多种订阅计划。付费订阅按月或按年计费，
              您可以在设置中随时取消订阅，取消后将在当前计费周期结束时生效。
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">5. 数据所有权</h2>
            <p className="leading-relaxed">
              您上传的数据归您所有。我们不会将您的数据用于训练 AI 模型，
              也不会向第三方出售您的数据。我们仅在提供服务所必需的范围内处理您的数据。
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">6. 服务变更与终止</h2>
            <p className="leading-relaxed">
              我们保留随时修改或终止服务的权利。如服务条款发生重大变更，
              我们将提前通知您。您也可以随时停止使用本服务。
            </p>
          </section>

          <div className="text-xs text-zinc-400 pt-4 border-t border-zinc-100">
            最后更新日期：2026年3月1日
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ==================== 隐私政策页面 ====================

function PrivacyPage({ onBack }: { onBack: () => void }) {
  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <CaretRight className="w-5 h-5 text-zinc-600 rotate-180" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">隐私政策</h1>
            <p className="text-sm text-zinc-500">Privacy Policy</p>
          </div>
        </div>

        {/* 内容 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 space-y-6 text-sm text-zinc-700">
          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">1. 信息收集</h2>
            <p className="leading-relaxed mb-2">
              我们收集以下类型的信息：
            </p>
            <ul className="list-disc list-inside space-y-2 leading-relaxed">
              <li>账户信息：用户名、邮箱地址、密码</li>
              <li>使用数据：工作流执行记录、功能使用统计</li>
              <li>上传数据：您上传的 Excel/CSV 文件（仅用于处理，不用于其他目的）</li>
              <li>设备信息：浏览器类型、IP 地址（用于安全和服务优化）</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">2. 信息使用</h2>
            <p className="leading-relaxed">
              我们使用您的信息来：
            </p>
            <ul className="list-disc list-inside space-y-2 leading-relaxed mt-2">
              <li>提供、维护和改进我们的服务</li>
              <li>处理您的工作流请求</li>
              <li>发送服务通知和更新</li>
              <li>防止欺诈和滥用</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">3. 数据安全</h2>
            <p className="leading-relaxed">
              我们采用行业标准的安全措施保护您的数据，包括加密传输、访问控制和安全审计。
              您的文件在处理后会在 24 小时内自动删除，除非您选择保存。
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">4. 数据共享</h2>
            <p className="leading-relaxed">
              我们不会将您的个人数据出售给第三方。仅在以下情况下可能共享数据：
            </p>
            <ul className="list-disc list-inside space-y-2 leading-relaxed mt-2">
              <li>经您明确同意</li>
              <li>遵守法律法规要求</li>
              <li>保护我们的合法权益</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">5. 您的权利</h2>
            <p className="leading-relaxed">
              您有权：访问、更正或删除您的个人数据；导出您的数据；
              撤回同意；以及向监管机构投诉。
            </p>
          </section>
          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3">6. Cookie 使用</h2>
            <p className="leading-relaxed">
              我们使用 Cookie 和类似技术来提供基本功能、记住您的偏好和分析使用情况。
              您可以在浏览器设置中管理 Cookie 偏好。
            </p>
          </section>

          <div className="text-xs text-zinc-400 pt-4 border-t border-zinc-100">
            最后更新日期：2026年3月1日
          </div>
        </div>
      </motion.div>
    </div>
  );
}
